/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'
import swal from 'sweetalert2'

import {
  Box, Button, FormField, LoadingBubbles, SidebarModal, SidebarNoticeBox, Text,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'

import { getUppyResourceOptions, getComponentCallbacks } from '@functions/uploadModal'
import api from '@functions/api'

import activityLogTypes from '@enums/activityLogTypes'

import useCurrentUser from '@hooks/useCurrentUser'
import useReduxAction from '@hooks/useReduxAction'
import useUppyUploadResource from '@hooks/useUppyUploadResource'

import CustomUploader from './components/CustomUploader'
import ResourcePrompts from './components/ResourcePrompts'
import FailedItemList from './components/FailedItemList'
import RecentlyUploadedList from './components/RecentlyUploadedList'

const defaultState = {
  addedCount: [],
  allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/DNG', 'image/CR2', 'image/ARW'],
  allowUpload: true,
  currentlyAddedResource: null,
  doneUpload: false,
  lastDeletedId: 0,
  lastFailedBatch: [],
  lastUploadedBatch: [],
  recentlyFailed: [],
  recentlyUploaded: [],
  resourceOptions: [],
  showUploadNewBatch: false,
  isOffline: false,
  uploadLoading: false,
}

const insertRecentlyUploadedBatch = (newUploadList, uploadList, newFailedList, failedList, setState) => {
  setState({ lastUploadedBatch: [], lastFailedBatch: [], doneUpload: false })
  setState({
    recentlyUploaded: uploadList.concat(newUploadList),
    recentlyFailed: failedList.concat(newFailedList),
  })
}

const removeRecentlyDeleted = (id, list, setState) => {
  setState({ lastDeletedId: 0 })
  setState({ recentlyUploaded: list.filter(x => x.id !== id) })
}

const formatResourceNames = (resourceTypes, conjunction) => {
  const names = resourceTypes.map(x => x.description)

  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`
    : names[0]
}

const UploadResourceModal = (props) => {
  const {
    callbacks,
    modalKey,
    serviceJob,
    showModal,
    title,
    titleSecondLine,
    serviceJobOptions,
    type,
    fileTypesAllowed,
    resourceTypes,
  } = props

  const { checkProgress, checkUploadStatus, deleteFn, scanFolderFn } = callbacks

  const entities = useSelector(reduxState => reduxState.entities)
  const { resources, fileServiceContainers } = entities

  const { currentUser } = useCurrentUser()

  const [state, setState] = useSetState(defaultState)
  const {
    addedCount,
    allowedFileTypes,
    allowUpload,
    currentlyAddedResource,
    doneUpload,
    lastDeletedId,
    lastFailedBatch,
    lastUploadedBatch,
    recentlyFailed,
    recentlyUploaded,
    resourceOptions,
    showUploadNewBatch,
    isOffline,
    uploadLoading,
  } = state

  const containerName = `service-jobs-${DateTime.fromISO(serviceJob.createdAt, { zone: 'utc' }).toFormat('yyyy')}`

  const { loading } = useReduxAction('fileServiceContainers', 'loadFileServiceContainer', containerName, [])
  const { loading: resourcesloading, scanning } = useSelector(reduxState => reduxState.resources)

  const { loading: serviceJobLoading } = useReduxAction('serviceJobs', 'loadServiceJobV1', {
    includes: ['service', 'campaign', 'client', 'details', 'users', 'notes'].join(','),
  }, [serviceJob.id, doneUpload], {
    dispatchAction: (action, requestOptions) => action(serviceJob.id, requestOptions),
    shouldPerformFn: () => doneUpload && type === 'Resources',
  })

  useEffect(() => {
    if (lastDeletedId > 0) {
      removeRecentlyDeleted(lastDeletedId, recentlyUploaded, setState)
    }
  }, [resources])

  useEffect(() => {
    if (fileTypesAllowed) {
      setState({ allowedFileTypes: fileTypesAllowed.split(',') })
    }
  }, [fileTypesAllowed])

  useEffect(() => {
    if (doneUpload) {
      setState({ showUploadNewBatch: true, allowUpload: false })

      const hasUploadedBatch = Array.isArray(lastUploadedBatch) && lastUploadedBatch.length > 0
      const hasFailedBatch = Array.isArray(lastFailedBatch) && lastFailedBatch.length > 0

      if (hasUploadedBatch || hasFailedBatch) {
        insertRecentlyUploadedBatch(
          hasUploadedBatch ? lastUploadedBatch : [],
          recentlyUploaded,
          hasFailedBatch ? lastFailedBatch : [],
          recentlyFailed,
          setState,
        )
      }
    }
  }, [doneUpload, lastUploadedBatch, lastFailedBatch, recentlyUploaded, recentlyFailed, setState])

  const fileServiceContainer = Object.values(fileServiceContainers).find(x => x.container === containerName)
  const uppyCallbacks = { ...getComponentCallbacks('uppy', setState) }
  const {
    instances: uppyInstances,
    lastError: lastUploadError,
    retryAllUploads,
    clearLastError,
  } = useUppyUploadResource(resourceOptions)

  const filteredUppyInstances = useMemo(
    () => (uppyInstances ?? []).filter(x => x !== null && x !== undefined),
    [uppyInstances],
  )

  const uploadedResourcesRef = useRef([])
  const uploadPercentagesRef = useRef({})
  const averagePercentageRef = useRef()
  const lastDisplayedPercentageRef = useRef(0)
  const isOfflineRef = useRef(typeof navigator !== 'undefined' ? !navigator.onLine : false)
  const pendingScanFailedItemsRef = useRef(null)
  const scanRetryTimeoutRef = useRef(null)

  useEffect(() => {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine
    isOfflineRef.current = offline

    if (offline) {
      setState({ isOffline: true })
    }
  }, [setState])

  const setProgressToZero = useCallback(() => {
    checkProgress(0)
    lastDisplayedPercentageRef.current = 0
    if (averagePercentageRef.current) {
      averagePercentageRef.current.textContent = '0%'
    }
  }, [checkProgress])

  const finalizeCompletedUploads = useCallback((failedItems) => {
    scanFolderFn(serviceJob.id).then(({ success, errors, data }) => {
      if (!success && errors) {
        toast.warn(errors[0])
        pendingScanFailedItemsRef.current = failedItems
        const online = typeof navigator !== 'undefined' ? navigator.onLine : true
        if (online && !scanRetryTimeoutRef.current) {
          scanRetryTimeoutRef.current = window.setTimeout(() => {
            scanRetryTimeoutRef.current = null
            if (pendingScanFailedItemsRef.current) {
              finalizeCompletedUploads(pendingScanFailedItemsRef.current)
            }
          }, 2000)
        }
      } else {
        pendingScanFailedItemsRef.current = null
        if (scanRetryTimeoutRef.current) {
          window.clearTimeout(scanRetryTimeoutRef.current)
          scanRetryTimeoutRef.current = null
        }
      }

      uploadPercentagesRef.current = {}
      uploadedResourcesRef.current = []
      setProgressToZero()

      const hasFailedItems = Array.isArray(failedItems) && failedItems.length > 0

      setState({
        uploadLoading: false,
        doneUpload: true,
        lastUploadedBatch: Array.isArray(data) ? data : [],
        ...(hasFailedItems ? { lastFailedBatch: [...new Set(failedItems)] } : { lastFailedBatch: [] }),
      })
    })
  }, [scanFolderFn, serviceJob.id, checkProgress, setState])

  const onCompleteUploads = useCallback((failedItems) => {
    const offline = isOfflineRef.current || (typeof navigator !== 'undefined' && !navigator.onLine)

    if (offline) {
      pendingScanFailedItemsRef.current = failedItems
      setState({ uploadLoading: false, isOffline: true })
      return
    }

    pendingScanFailedItemsRef.current = null
    finalizeCompletedUploads(failedItems)
  }, [finalizeCompletedUploads, setState])

  const hasPendingFiles = useCallback(() => filteredUppyInstances.some((instance) => {
    const files = instance?.getFiles?.() || []
    return files.some(file => !file?.error && file?.progress?.uploadComplete !== true)
  }), [filteredUppyInstances])

  useEffect(() => {
    if (!filteredUppyInstances?.length) {
      return undefined
    }

    const cleanupCallbacks = filteredUppyInstances.map((uppyInstance) => {
      const handleUploadProgress = (data) => {
        if (isOfflineRef.current) {
          return
        }

        const uploader = uppyInstance?.opts?.id?.split('-')
        const uploaderId = uploader.length ? uploader[1] : null
        const progressValue = typeof data === 'number'
          ? data
          : typeof data?.percentage === 'number'
            ? data.percentage
            : null

        if (progressValue !== null && uploaderId && uploadPercentagesRef.current[uploaderId] !== undefined) {
          uploadPercentagesRef.current[uploaderId] = Math.max(uploadPercentagesRef.current[uploaderId], progressValue)
        }

        if (averagePercentageRef.current) {
          const percentages = Object.values(uploadPercentagesRef.current)
          if (percentages.length) {
            const total = percentages.reduce((sum, percentage) => sum + percentage, 0)
            const average = percentages.length && total > 0 ? (total / percentages.length).toFixed(0) : '0'
            const displayPercentage = Math.min(99, Math.max(Number(average), lastDisplayedPercentageRef.current))
            checkProgress(displayPercentage)
            averagePercentageRef.current.textContent = `${displayPercentage}%`
            lastDisplayedPercentageRef.current = displayPercentage
          } else {
            checkProgress(lastDisplayedPercentageRef.current)
            averagePercentageRef.current.textContent = `${lastDisplayedPercentageRef.current}%`
          }
        }
      }

      const handleUploadComplete = (result) => {
        if (result) {
          const uploadedFile = {}

          if (result.successful.length) {
            uploadedFile.resourceType = result.successful[0].meta.resourceTypeId
            uploadedFile.successful = result.successful
          }
          if (result.failed.length) {
            uploadedFile.failed = result.failed
          }

          if (!uploadedFile.resourceType && result.failed[0]?.meta?.resourceTypeId) {
            uploadedFile.resourceType = result.failed[0].meta.resourceTypeId
          }

          if (uploadedFile?.resourceType) {
            const existingIndex = uploadedResourcesRef.current.findIndex(x => x.resourceType === uploadedFile.resourceType)
            if (existingIndex === -1) {
              uploadedResourcesRef.current.push(uploadedFile)
            } else {
              uploadedResourcesRef.current[existingIndex] = {
                ...uploadedResourcesRef.current[existingIndex],
                ...uploadedFile,
              }
            }
          }
        }

        if (uploadedResourcesRef.current.length === addedCount.length && uploadLoading && !hasPendingFiles()) {
          onCompleteUploads(
            uploadedResourcesRef.current
              .flatMap(item => item.failed)
              .filter(x => x !== null && x !== undefined),
          )
        }
      }

      uppyInstance.on('progress', handleUploadProgress)
      uppyInstance.on('complete', handleUploadComplete)

      return () => {
        uppyInstance.off('progress', handleUploadProgress)
        uppyInstance.off('complete', handleUploadComplete)
      }
    })

    return () => {
      cleanupCallbacks.forEach(unsubscribe => unsubscribe && unsubscribe())
    }
  }, [filteredUppyInstances, addedCount, uploadLoading, checkProgress, onCompleteUploads])

  useEffect(() => {
    checkUploadStatus(recentlyUploaded)
  }, [recentlyUploaded])

  const handleRetryUploads = useCallback(({ silent = false, resetProgress = true } = {}) => {
    const retried = retryAllUploads()
    if (retried) {
      const hasActiveUploads = filteredUppyInstances.some((instance) => {
        const files = instance?.getFiles?.() || []
        return files.some(file => !file?.error && file?.progress?.uploadComplete !== true)
      })

      const retryableInstances = filteredUppyInstances.filter((instance) => {
        const files = instance?.getFiles?.() || []

        return files.some(file => file?.error || file?.progress?.uploadComplete === false)
      })

      const instancesForProgress = retryableInstances.length
        ? retryableInstances
        : filteredUppyInstances.filter(instance => instance?.getFiles?.()?.length)

      uploadedResourcesRef.current = []
      if (!hasActiveUploads && resetProgress) {
        uploadPercentagesRef.current = instancesForProgress.reduce((accumulator, instance) => {
          const uploaderParts = instance?.opts?.id?.split('-') || []
          const uploaderId = uploaderParts.length > 1 ? uploaderParts[1] : null

          if (uploaderId) {
            accumulator[uploaderId] = 0
          }

          return accumulator
        }, {})
        checkProgress(0)
        setProgressToZero()
      }
      setState({ uploadLoading: true })
    } else {
      if (!silent) {
        toast.info('No pending uploads to retry.')
      }
    }
  }, [retryAllUploads, filteredUppyInstances, checkProgress, setState])

  useEffect(() => {
    if (lastUploadError) {
      if (lastUploadError?.isNetworkError) {
        isOfflineRef.current = true
        setState({ isOffline: true })
      }
      setState({ uploadLoading: false })
      handleRetryUploads({ silent: true, resetProgress: false })
    }
  }, [lastUploadError, handleRetryUploads, setState])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    let pollId = null
    const startPolling = () => {
      if (pollId) {
        return
      }
      pollId = window.setInterval(checkConnection, 1500)
    }

    const stopPolling = () => {
      if (!pollId) {
        return
      }
      window.clearInterval(pollId)
      pollId = null
    }

    const handleOffline = () => {
      isOfflineRef.current = true
      setState({ isOffline: true })
      startPolling()
    }

    const handleOnline = () => {
      isOfflineRef.current = false
      setState({ isOffline: false })
      stopPolling()
      const hadPendingScan = !!pendingScanFailedItemsRef.current

      if (pendingScanFailedItemsRef.current) {
        const pendingFailedItems = pendingScanFailedItemsRef.current
        pendingScanFailedItemsRef.current = null
        finalizeCompletedUploads(pendingFailedItems)
      }

      if (!hadPendingScan && uploadLoading && !hasPendingFiles()) {
        onCompleteUploads([])
      }

      if (!hadPendingScan && (lastUploadError || uploadLoading)) {
        handleRetryUploads({ silent: true, resetProgress: false })
      }
    }

    const checkConnection = () => {
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true
      if (!online && !isOfflineRef.current) {
        handleOffline()
      } else if (online && isOfflineRef.current) {
        handleOnline()
      }
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    window.addEventListener('focus', checkConnection)
    document.addEventListener('visibilitychange', checkConnection)

    checkConnection()

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('focus', checkConnection)
      document.removeEventListener('visibilitychange', checkConnection)
      stopPolling()
    }
  }, [lastUploadError, uploadLoading, handleRetryUploads, finalizeCompletedUploads, onCompleteUploads, hasPendingFiles, setState])

  const resetUploader = () => {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine

    isOfflineRef.current = offline

    clearLastError()
    pendingScanFailedItemsRef.current = null
    if (scanRetryTimeoutRef.current) {
      window.clearTimeout(scanRetryTimeoutRef.current)
      scanRetryTimeoutRef.current = null
    }
    uploadedResourcesRef.current = []
    uploadPercentagesRef.current = {}
    setProgressToZero()
    setState({
      addedCount: [],
      allowUpload: true,
      currentlyAddedResource: null,
      doneUpload: false,
      lastDeletedId: 0,
      lastFailedBatch: [],
      lastUploadedBatch: [],
      recentlyFailed: [],
      recentlyUploaded: [],
      resourceOptions: [],
      showUploadNewBatch: false,
      isOffline: offline,
      uploadLoading: false,
    })
  }


  useEffect(() => {
    if (resourceTypes.length && allowUpload) {
      const resourceUppyOptions = resourceTypes
        .map(x => (getUppyResourceOptions(
          allowedFileTypes,
          serviceJobOptions,
          uppyCallbacks,
          x.id,
          fileServiceContainer,
          currentUser.id,
        )))
      setState({ resourceOptions: resourceUppyOptions })
    }
  }, [resourceTypes, allowUpload])

  const totalAddedFiles = addedCount?.reduce((accumulator, current) => accumulator + current.count, 0)

  const missingResources = resourceTypes.filter(resource =>
    !addedCount.some(x => x.resource === resource.id && x.count > 0))
  const missingOptionalResources = missingResources.filter(resource => ['Flash', 'Clones'].includes(resource.name))

  const handleUpload = () => {
    if (!filteredUppyInstances?.length) {
      return
    }

    const uploadableInstances = filteredUppyInstances.filter(instance => instance?.getFiles()?.length)

    if (!uploadableInstances.length) {
      toast.info('Add files before uploading.')
      return
    }

    const startUpload = () => {
      uploadedResourcesRef.current = []
      uploadPercentagesRef.current = uploadableInstances.reduce((accumulator, instance) => {
        const uploaderParts = instance?.opts?.id?.split('-') || []
        const uploaderId = uploaderParts.length > 1 ? uploaderParts[1] : null

        if (uploaderId) {
          accumulator[uploaderId] = 0
        }

        return accumulator
      }, {})

      if (averagePercentageRef.current) {
        averagePercentageRef.current.textContent = '0%'
      }

      setProgressToZero()
      setState({ uploadLoading: true })
      uploadableInstances.forEach(instance => instance?.upload?.())
    }

    if (!missingOptionalResources.length) {
      startUpload()
      return
    }

    const missingNamesAnd = formatResourceNames(missingOptionalResources, 'and')
    const missingNamesOr = formatResourceNames(missingOptionalResources, 'or')

    swal.fire({
      title: `No ${missingNamesAnd} Images Added`,
      icon: 'warning',
      html: `
        <div style="text-align:left;">
          <p style="margin:0 0 12px;text-align:center;">There are no ${missingNamesAnd} images added. Please go back and add these images if you have them.</p>
          <div style="background:#fff4e5;border-radius:4px;padding:10px;margin-bottom:12px;font-size:13px;color:#8a5a00;">
            Once you click Upload, this order will be sent to production automatically
          </div>
          <label style="display:flex;align-items:flex-start;gap:8px;font-size:13px;text-align:left;">
            <input type="checkbox" id="confirmMissingResources" style="margin-top:2px;" />
            I confirm that I do not have any ${missingNamesAnd} Images to upload and that the resources are now fully complete.
          </label>
        </div>`,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Confirm and Upload',
      cancelButtonText: 'Go Back and Add Images',
      willOpen: () => {
        const confirmButton = swal.getConfirmButton()
        const confirmCheckbox = swal.getHtmlContainer().querySelector('#confirmMissingResources')

        const updateConfirmState = () => {
          confirmButton.disabled = !confirmCheckbox?.checked
        }

        confirmCheckbox?.addEventListener('change', updateConfirmState)
        updateConfirmState()
      },
      preConfirm: () => {
        const confirmCheckbox = swal.getHtmlContainer().querySelector('#confirmMissingResources')

        if (!confirmCheckbox?.checked) {
          swal.showValidationMessage('Please confirm you do not have these images.')
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        api('/v1/activity-logs', {}, {
          method: 'POST',
          data: {
            serviceJobId: serviceJob.id,
            serviceJobStatusId: serviceJob.serviceJobStatusId,
            activityLogTypeId: activityLogTypes.find(x => x.name === 'ConfirmMissingOptionalResources')?.id,
            description: `Confirmed no ${missingNamesOr} images to upload.`,
          },
        }).catch(error => console.error('Failed to create activity log:', error))

        startUpload()
      }
    })
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header callbacks={callbacks} title={title} titleSecondLine={titleSecondLine} />
      <SidebarModal.Content hasFooter={!!(showUploadNewBatch || totalAddedFiles)}>
        {((loading || resourcesloading) && !uploadLoading) && <LoadingBubbles color="rgba(0, 0, 0, 0.15)" style={{ flexShrink: 0 }} />}
        <Box flexDirection="column" flexShrink={0}>
          <ResourcePrompts
            resourceTypes={resourceTypes}
            serviceJob={serviceJob}
            uploadState={state}
          />
          {(lastUploadError || (isOffline && uploadLoading)) && (
            <SidebarNoticeBox boxProps={{ marginTop: 'medium' }}>
              <SidebarNoticeBox.Title>Upload error</SidebarNoticeBox.Title>
              <SidebarNoticeBox.Paragraph>
                {(isOffline || lastUploadError?.isNetworkError)
                  ? 'Connection lost.. Upload will resume automatically once reconnected.'
                  : 'Some files failed to upload. Automatic retry attempts will be made.'}
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}
        </Box>
        {!loading && allowUpload && (
          <Box flexDirection="column" marginBottom="large">
            {filteredUppyInstances.length && filteredUppyInstances?.length === resourceTypes.length && (
              <FormField direction="column" marginTop="medium">
                {resourceTypes.map(resource => (
                  <CustomUploader
                    key={resource.id}
                    currentlyAddedResource={currentlyAddedResource}
                    resource={resource}
                    uploadLoading={uploadLoading}
                    callbacks={{
                      handleAddFiles: fileCount => setState({ addedCount: [...addedCount, fileCount], currentlyAddedResource: null }),
                    }}
                    uppy={filteredUppyInstances.find(x => x?.opts?.id?.includes(resource.id.toString()))}
                  />
                ))}
              </FormField>
            )}
            {filteredUppyInstances?.length !== resourceTypes.length && (
              <FormField direction="column" marginTop="large">
                <Text marginBottom="small" fontSize="xsmall">
                  Could not load uppy service
                </Text>
              </FormField>
            )}
          </Box>
        )}

        {recentlyFailed.length > 0 && (
          <SidebarModal.ExpandableSectionBox label={`Failed Items (${recentlyFailed.length})`}>
            <FailedItemList list={recentlyFailed} />
          </SidebarModal.ExpandableSectionBox>
        )}

        {recentlyUploaded.length > 0 && (
          <SidebarModal.ExpandableSectionBox label={`Uploaded Items (${recentlyUploaded.length})`}>
            <RecentlyUploadedList
              list={recentlyUploaded}
              callbacks={{ ...getComponentCallbacks('recentlyuploadedlist', setState), deleteFn }}
              fileType={type}
            />
          </SidebarModal.ExpandableSectionBox>
        )}
      </SidebarModal.Content>

      {showUploadNewBatch && (
        <SidebarModal.Footer>
          <Button
            buttonStyle="primaryCreate"
            onClick={() => resetUploader()}
            size="medium"
            loading={serviceJobLoading || loading || resourcesloading}
          >
            Upload New Batch
          </Button>
        </SidebarModal.Footer>
      )}

      {totalAddedFiles && !doneUpload && !(recentlyUploaded.length || recentlyFailed.length) && (
        <SidebarModal.Footer>
          <Button
            buttonStyle="primaryCreate"
            onClick={() => handleUpload()}
            size="medium"
            disabled={uploadLoading || loading || scanning || isOffline || (typeof navigator !== 'undefined' && !navigator.onLine)}
          >
            {uploadLoading || loading || scanning || isOffline || (typeof navigator !== 'undefined' && !navigator.onLine) ? (
              scanning ? <Text color="lightGrey">Finalizing... Please wait</Text> : (
                <Text ref={averagePercentageRef} color="lightGrey">
                  {(isOffline || (typeof navigator !== 'undefined' && !navigator.onLine) || lastUploadError?.isNetworkError) ? 'Connection lost.. Upload will resume automatically once reconnected.' : 'Uploading...'}
                </Text>
              )
            ) : <Text color="white">{`Upload ${totalAddedFiles} File/s`}</Text>}
          </Button>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

UploadResourceModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  serviceJobOptions: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
  serviceJob: PropTypes.object,
  title: PropTypes.string,
  titleSecondLine: PropTypes.string,
  resourceTypes: PropTypes.array,
  fileTypesAllowed: PropTypes.string,
}

UploadResourceModal.defaultProps = {
  modalKey: 'UploadResourceModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <UploadResourceModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
